import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LocalAgentService } from './local-agent.service';

/**
 * Formato estándar de respuesta TokenSafe (agente).
 * HTTP 200 con body: status 200 = OK; 4xx/5xx = error. Mostrar mensaje al usuario.
 */
export interface TokenSafeResponse<T = unknown> {
  status: number;
  mensaje: string;
  respuesta: T | false;
}

/** Respuesta exitosa de validacion-id: respuesta.correoSolicitud (y opcional numSolicitud). */
export interface ValidacionSolicitudRespuesta {
  correoSolicitud?: string;
  numSolicitud?: string;
  correo?: string;
  [key: string]: unknown;
}

/** Respuesta exitosa de validacion-correo: ID de transacción. */
export interface ValidacionCorreoRespuesta {
  transacion?: string;
  idTransaccion?: string;
  transactionId?: string;
  transaction?: string;
  [key: string]: unknown;
}

/** Respuesta exitosa de validar-otp: JWT y datos del titular. */
export interface OtpValidarRespuesta {
  token?: string;
  solicitante?: string;
  cedula?: string;
  nit?: string;
  [key: string]: unknown;
}

/** Respuesta de descargar-guardar: pfxPath y password para import-pfx. */
export interface DescargarGuardarRespuesta {
  success: boolean;
  pfxPath?: string;
  password?: string;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TokensafeService {
  constructor(private agent: LocalAgentService) {}

  private isSuccess<T>(res: TokenSafeResponse<T>): res is TokenSafeResponse<T> & { respuesta: T } {
    return res.status === 200 && res.respuesta !== false && res.respuesta !== null;
  }

  private handleResponse<T>(res: TokenSafeResponse<T>): T {
    if (this.isSuccess(res)) return res.respuesta;
    throw new Error(res.mensaje || 'Error en la operación');
  }

  private toError(err: unknown, fallback: string): Error {
    if (err instanceof Error) return err;
    const e = err as { error?: { mensaje?: string; errorMessage?: string; message?: string }; message?: string };
    const msg =
      e?.error?.mensaje ||
      e?.error?.errorMessage ||
      e?.error?.message ||
      e?.message ||
      fallback;
    return new Error(typeof msg === 'string' ? msg : fallback);
  }

  /**
   * Paso 1: Validar solicitud (obtener correo).
   * POST /api/tokensafe/validacion-id — numSolicitud obligatorio.
   */
  validarSolicitud(numSolicitud: string): Observable<ValidacionSolicitudRespuesta> {
    const n = (numSolicitud ?? '').trim();
    if (!n) return throwError(() => new Error('El número de solicitud es obligatorio'));
    return this.agent.postToAgent<TokenSafeResponse<ValidacionSolicitudRespuesta>>(
      '/api/tokensafe/validacion-id',
      { numSolicitud: n }
    ).pipe(
      map(res => this.handleResponse(res)),
      catchError(err => throwError(() => this.toError(err, 'Error al validar la solicitud')))
    );
  }

  /**
   * Paso 2: Validar correo (obtener ID de transacción).
   * POST /api/tokensafe/validacion-correo — numSolicitud y correoSolicitud obligatorios.
   */
  validarCorreo(numSolicitud: string, correoSolicitud: string): Observable<ValidacionCorreoRespuesta> {
    const num = (numSolicitud ?? '').trim();
    const correo = (correoSolicitud ?? '').trim();
    if (!num) return throwError(() => new Error('El número de solicitud es obligatorio'));
    if (!correo) return throwError(() => new Error('El correo electrónico es obligatorio'));
    return this.agent.postToAgent<TokenSafeResponse<ValidacionCorreoRespuesta>>(
      '/api/tokensafe/validacion-correo',
      { numSolicitud: num, correoSolicitud: correo }
    ).pipe(
      map(res => this.handleResponse(res)),
      catchError(err => throwError(() => this.toError(err, 'Error al validar correo')))
    );
  }

  /**
   * Paso 3: Validar OTP. Devuelve JWT para siguientes pasos.
   * POST /api/tokensafe/validar-otp — numSolicitud, transacion y codigoOtp obligatorios.
   */
  validarOtp(numSolicitud: string, transacion: string, codigoOtp: string): Observable<OtpValidarRespuesta> {
    const num = (numSolicitud ?? '').trim();
    const trans = (transacion ?? '').trim();
    const codigo = (codigoOtp ?? '').trim();
    if (!num) return throwError(() => new Error('El número de solicitud es obligatorio'));
    if (!trans) return throwError(() => new Error('El identificador de transacción es obligatorio'));
    if (!codigo) return throwError(() => new Error('El código OTP es obligatorio'));
    return this.agent.postToAgent<TokenSafeResponse<OtpValidarRespuesta>>(
      '/api/tokensafe/validar-otp',
      { numSolicitud: num, transacion: trans, codigoOtp: codigo }
    ).pipe(
      map(res => this.handleResponse(res)),
      catchError(err => throwError(() => this.toError(err, 'Error al validar OTP')))
    );
  }

  /**
   * Paso 4: Descargar y guardar certificado.
   * POST /api/tokensafe/descargar-guardar
   */
  descargarGuardar(params: {
    numSolicitud?: string;
    transacion?: string;
    codigoOtp?: string;
    token?: string;
  }): Observable<DescargarGuardarRespuesta> {
    const num = (params?.numSolicitud ?? '').trim();
    const trans = (params?.transacion ?? '').trim();
    const codigo = (params?.codigoOtp ?? '').trim();
    const token = (params?.token ?? '').trim();
    if (!num) return throwError(() => new Error('El número de solicitud es obligatorio'));
    if (!trans) return throwError(() => new Error('El identificador de transacción es obligatorio'));
    if (!codigo) return throwError(() => new Error('El código OTP es obligatorio'));
    if (!token) return throwError(() => new Error('El token de sesión es obligatorio'));
    return this.agent.postToAgent<DescargarGuardarRespuesta>(
      '/api/tokensafe/descargar-guardar',
      { numSolicitud: num, transacion: trans, codigoOtp: codigo, token },
      120000
    ).pipe(
      map(res => {
        if (res?.success && res?.pfxPath != null && res?.password != null) {
          return { success: true, pfxPath: res.pfxPath, password: res.password };
        }
        throw new Error(res?.errorMessage || 'Error al descargar y guardar el certificado');
      }),
      catchError(err => throwError(() => this.toError(err, 'Error al descargar y guardar')))
    );
  }
}
